import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { Breadcrumbs, Link, Divider, Tabs, TabList, Tab, TabPanel, Slider, Button, Modal, ModalDialog, ModalClose } from "@mui/joy";
import Box from "@mui/joy/Box";
import Typography from "@mui/joy/Typography";
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import CalendarMonthRounded from '@mui/icons-material/CalendarMonthRounded';
import Table from '@mui/joy/Table';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CircularProgress from '@mui/joy/CircularProgress';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Tooltip from '@mui/joy/Tooltip';
import { Dashboard, PersonOff, Calculate, PriceCheck, Sms, ToggleOn, Map as MapIcon, WhatsApp } from '@mui/icons-material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CampaignIcon from '@mui/icons-material/Campaign';
import Switch from '@mui/joy/Switch';
import Input from '@mui/joy/Input';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Grid from '@mui/joy/Grid';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Checkbox from '@mui/joy/Checkbox';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRangePicker, RangeKeyDict, Range, defaultStaticRanges } from 'react-date-range';
import { addDays, eachDayOfInterval, endOfYear, format as formatDateFns, getDay, startOfYear, subYears } from 'date-fns';
import { enGB } from 'date-fns/locale';
import ApiService from "../../axios/Bookings-Axios";
import UnassignedLeads from './UnassignedLeads';
import BulkSMS from './BulkSMS';
import BulkWhatsApp from './BulkWhatsApp';

const TerritoryMap = React.lazy(() => import('./TerritoryMap'));

// --- Types ---
interface Metrics {
    bookings_7: number;
    bookings_14: number;
    bookings_30: number;
    revenue_30: number;
    spend_30: number;
    roas: number;
}

interface Deviations {
    bookings_7: number;
    bookings_14: number;
    bookings_30: number;
    roas: number;
}

interface FranchiseData {
    id: number;
    location: string;
    metrics: Metrics;
    deviation: Deviations;
}

interface NetworkOverviewResponse {
    network_averages: Metrics;
    franchises: FranchiseData[];
}

interface CampaignData {
    id: number;
    campaign_id: string;
    service: string;
    franchise: string;
    spend: number;
    platform_leads: number;
    internal_leads: number;
    bookings_count: number;
    value_booked: number;
    roas: number;
    cpl: number;
    cpa: number;
    conversion_rate: number;
    status: string;
}

interface CampaignLeakageExampleLead {
    id: number;
    customer: string;
    services: string[];
    created_at: string;
}

interface CampaignLeakageRow {
    id: number;
    campaign_name: string;
    campaign_franchise: string;
    lead_franchise: string;
    advertising_source: string;
    lead_count: number;
    latest_created_at: string;
    example_leads: CampaignLeakageExampleLead[];
}

interface CampaignLeakageResponse {
    date_range: { start_date: string; end_date: string };
    summary: {
        compared_leads: number;
        mismatched_leads: number;
        unrecognised_campaign_leads: number;
        mismatch_groups: number;
    };
    rows: CampaignLeakageRow[];
}

interface AdPlatformFranchiseRow {
    id: number;
    location: string;
    roof_ads_status: string;
    facebook_sales_hours_enabled: boolean;
    google_sales_hours_enabled: boolean;
    sms_campaign_enabled: boolean;
    has_facebook_campaign: boolean;
    has_google_campaign: boolean;
}

interface SalespersonOption {
    id: number;
    name: string;
    username: string;
}

interface SalesPerformanceMetricWithLeads {
    average: number;
    totalLeads: number;
    totalCalls: number;
}

interface SalesPerformanceConversionMetric {
    leads: number;
    bookings: number;
    rate: number;
}

interface SalesPerformanceDay {
    date: string;
    averageSpeedSeconds: number | null;
    formattedAverageSpeed: string | null;
    leadCount: number;
}

interface SalesWorkedHoursDay {
    date: string;
    workedSeconds: number | null;
    formattedWorkedTime: string | null;
    totalCallSeconds: number;
    formattedCallTime: string | null;
    callCount: number;
    firstCallAt: string | null;
    lastCallAt: string | null;
}

interface SalesPerformanceResponse {
    salespeople: SalespersonOption[];
    selectedSalespersonId: number | null;
    dateRange: { startDate: string; endDate: string };
    metrics: {
        totalLeads: number;
        averageCallsPerLead: number;
        averagePhoneCallLengthSeconds: number | null;
        totalPhoneCallLengthSeconds: number;
        averageCalls: SalesPerformanceMetricWithLeads;
        averageAttemptedCalls: SalesPerformanceMetricWithLeads;
        googleConversionRate: SalesPerformanceConversionMetric;
        facebookConversionRate: SalesPerformanceConversionMetric;
    };
    speedToLead: {
        startDate: string;
        endDate: string;
        days: SalesPerformanceDay[];
    };
    workedHours: {
        startDate: string;
        endDate: string;
        days: SalesWorkedHoursDay[];
    };
}

interface DateRangeStateItem extends Range { }

type Order = 'asc' | 'desc';
// Keys we allow sorting by
type SortableKeys = keyof Metrics | 'location';

// --- Helper: Deviation Chip ---
const DeviationBadge = ({ value }: { value: number }) => {
    const isPositive = value >= 0;
    const color = isPositive ? 'success' : 'danger';
    const Icon = isPositive ? TrendingUpIcon : TrendingDownIcon;

    return (
        <Tooltip title={`${value.toFixed(1)}% from average`} size="sm">
            <Chip
                variant="soft"
                color={color}
                size="sm"
                sx={{
                    fontSize: '10px',
                    height: '20px',
                    ml: 1,
                    minWidth: '60px'
                }}
                startDecorator={<Icon style={{ fontSize: '12px' }} />}
            >
                {Math.abs(value).toFixed(0)}%
            </Chip>
        </Tooltip>
    );
};

// --- Helper: Date Range String ---
const getDateRangeLabel = (days: number, isFuture: boolean = false) => {
    const today = new Date();
    const otherDate = new Date();

    if (isFuture) {
        otherDate.setDate(today.getDate() + days);
    } else {
        otherDate.setDate(today.getDate() - days);
    }

    const options: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
    const start = isFuture ? today : otherDate;
    const end = isFuture ? otherDate : today;

    return `${start.toLocaleDateString('en-GB', options)} - ${end.toLocaleDateString('en-GB', options)}`;
};

const salesPerformanceStaticRanges = [
    ...defaultStaticRanges,
    {
        label: 'This Year',
        range: () => ({
            startDate: startOfYear(new Date()),
            endDate: new Date()
        }),
        isSelected: (range: Range) => {
            const thisYearStart = startOfYear(new Date());
            return range.startDate?.getTime() === thisYearStart.getTime() &&
                range.endDate?.toDateString() === new Date().toDateString();
        }
    },
    {
        label: 'Last Year',
        range: () => ({
            startDate: startOfYear(subYears(new Date(), 1)),
            endDate: endOfYear(subYears(new Date(), 1))
        }),
        isSelected: (range: Range) => {
            const lastYearStart = startOfYear(subYears(new Date(), 1));
            const lastYearEnd = endOfYear(subYears(new Date(), 1));
            return range.startDate?.getTime() === lastYearStart.getTime() &&
                range.endDate?.getTime() === lastYearEnd.getTime();
        }
    }
];

const formatDurationFromSeconds = (seconds: number | null | undefined): string => {
    if (seconds == null || !Number.isFinite(seconds)) return 'N/A';
    const rounded = Math.round(seconds);
    const minutes = Math.floor(rounded / 60);
    const remainingSeconds = rounded % 60;
    if (minutes >= 60) {
        const hours = Math.floor(minutes / 60);
        return `${hours}h ${minutes % 60}m ${remainingSeconds}s`;
    }
    if (minutes > 0) return `${minutes}m ${remainingSeconds}s`;
    return `${remainingSeconds}s`;
};

const formatMetricNumber = (value: number | null | undefined, decimals = 1): string => {
    return Number.isFinite(value) ? Number(value).toFixed(decimals) : 'N/A';
};

// Overview content component
function OverviewContent() {
    const [data, setData] = useState<NetworkOverviewResponse | null>(null);
    const [loading, setLoading] = useState(true);

    // Sorting State
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<SortableKeys>('bookings_7');

    useEffect(() => {
        const fetchData = async () => {
            try {
                ApiService.getAdminStatistics().then((data: NetworkOverviewResponse) => {
                    setData(data);
                    setLoading(false);
                });
            } catch (error) {
                console.error("Failed to fetch franchise stats", error);
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // --- Sorting Logic ---
    const handleRequestSort = (property: SortableKeys) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedData = useMemo(() => {
        if (!data) return [];

        return [...data.franchises].sort((a, b) => {
            let aValue: any;
            let bValue: any;

            // Handle location (top level) vs metrics (nested)
            if (orderBy === 'location') {
                aValue = a.location.toLowerCase();
                bValue = b.location.toLowerCase();
            } else {
                // All other sort keys are inside 'metrics'
                aValue = a.metrics[orderBy as keyof Metrics];
                bValue = b.metrics[orderBy as keyof Metrics];
            }

            if (bValue < aValue) {
                return order === 'asc' ? 1 : -1;
            }
            if (bValue > aValue) {
                return order === 'asc' ? -1 : 1;
            }
            return 0;
        });
    }, [data, order, orderBy]);

    // Helper component for Table Headers
    const SortableHeader = ({ id, label, align = 'center', width }: { id: SortableKeys, label: string, align?: any, width?: string }) => {
        const active = orderBy === id;
        return (
            <th
                style={{ width: width, textAlign: align, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleRequestSort(id)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : (align === 'center' ? 'center' : 'flex-start') }}>
                    {label}
                    {active ? (
                        order === 'desc' ?
                            <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.8 }} /> :
                            <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.8 }} />
                    ) : (
                        // Placeholder to keep height consistent
                        <Box sx={{ width: 16, height: 16, ml: 0.5 }} />
                    )}
                </Box>
            </th>
        );
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    }

    if (!data) return <Typography>No data available or permission denied.</Typography>;

    const past30Days = getDateRangeLabel(30, false);
    const next14Days = getDateRangeLabel(14, true);
    const next30Days = getDateRangeLabel(30, true);

    return (
        <>
            {/* 2. Network Averages Cards */}
            {/* Using CSS Grid for exact equal width/height distribution */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, // Stack on mobile, 3 cols on desktop
                gap: 2,
                mb: 3
            }}>
                {/* Avg 14-Day Bookings */}
                <Card variant="outlined" sx={{ boxShadow: 'sm' }}>
                    <CardContent>
                        <Typography level="title-sm" textColor="text.tertiary">Avg. 14-Day Bookings</Typography>
                        <Typography level="h3">{data.network_averages.bookings_14.toFixed(1)}</Typography>
                        <Typography level="body-xs" textColor="neutral.500">
                            {next14Days}
                        </Typography>
                    </CardContent>
                </Card>

                {/* Avg 30-Day Bookings */}
                <Card variant="outlined" sx={{ boxShadow: 'sm' }}>
                    <CardContent>
                        <Typography level="title-sm" textColor="text.tertiary">Avg. 30-Day Bookings</Typography>
                        <Typography level="h3">{data.network_averages.bookings_30.toFixed(1)}</Typography>
                        <Typography level="body-xs" textColor="neutral.500">
                            {next30Days}
                        </Typography>
                    </CardContent>
                </Card>

                {/* Financial Performance */}
                <Card variant="outlined" sx={{ boxShadow: 'sm' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography level="title-sm" textColor="text.tertiary">Avg. 30-Day Financials</Typography>
                            <Chip variant="soft" color={data.network_averages.roas > 3 ? 'success' : 'warning'} size="sm">
                                <b>{data.network_averages.roas.toFixed(2)}x ROAS</b>
                            </Chip>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 2 }}>
                            <Box>
                                <Typography level="h4">£{data.network_averages.spend_30.toFixed(0)}</Typography>
                                <Typography level="body-xs" textColor="text.tertiary">Spend</Typography>
                            </Box>

                            <Divider orientation="vertical" sx={{ height: '24px' }} />

                            <Box>
                                <Typography level="h4" color="success">£{data.network_averages.revenue_30.toFixed(0)}</Typography>
                                <Typography level="body-xs" textColor="text.tertiary">Revenue</Typography>
                            </Box>
                        </Box>
                        <Typography level="body-xs" textColor="neutral.500" sx={{ mt: 1 }}>
                            Last 30 days: {past30Days}
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* 3. Sortable Data Table */}
            <Sheet
                variant="outlined"
                sx={{
                    borderRadius: 'sm',
                    overflow: 'auto',
                    maxHeight: 'calc(100vh - 300px)',
                    boxShadow: 'sm'
                }}
            >
                <Table
                    stickyHeader
                    hoverRow
                    sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        fontSize: 'xs',
                        '& th': { fontWeight: 700 },
                    }}
                >
                    <thead>
                    <tr>
                        <SortableHeader id="location" label="Franchise" align="left" width="20%" />
                        <SortableHeader id="bookings_7" label="Jobs (Next 7d)" />
                        <SortableHeader id="bookings_14" label="Jobs (Next 14d)" />
                        <SortableHeader id="bookings_30" label="Jobs (Next 30d)" />
                        <SortableHeader id="spend_30" label="Spend (Last 30d)" align="right" />
                        <SortableHeader id="revenue_30" label="Value Booked (Last 30d)" align="right" />
                        <SortableHeader id="roas" label="ROAS (30d)" align="right" width="15%" />
                    </tr>
                    </thead>
                    <tbody>
                    {sortedData.map((row) => (
                        <tr key={row.id}>
                            <td>
                                <Typography fontWeight="lg" level="title-sm">{row.location}</Typography>
                            </td>

                            <td style={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography>{row.metrics.bookings_7}</Typography>
                                    <DeviationBadge value={row.deviation.bookings_7} />
                                </Box>
                            </td>

                            <td style={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography>{row.metrics.bookings_14}</Typography>
                                    <DeviationBadge value={row.deviation.bookings_14} />
                                </Box>
                            </td>

                            <td style={{ textAlign: 'center' }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography>{row.metrics.bookings_30}</Typography>
                                    <DeviationBadge value={row.deviation.bookings_30} />
                                </Box>
                            </td>

                            <td style={{ textAlign: 'right' }}>
                                <Typography level="body-xs">£{row.metrics.spend_30.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</Typography>
                            </td>

                            <td style={{ textAlign: 'right' }}>
                                <Typography level="body-xs" color="success">£{row.metrics.revenue_30.toLocaleString(undefined, {minimumFractionDigits: 0, maximumFractionDigits: 0})}</Typography>
                            </td>

                            <td>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                    <Typography fontWeight="lg">{row.metrics.roas.toFixed(1)}x</Typography>
                                    <DeviationBadge value={row.deviation.roas} />
                                </Box>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            </Sheet>
        </>
    );
}

function CampaignsContent() {
    const [days, setDays] = useState<number>(30);
    const [data, setData] = useState<CampaignData[]>([]);
    const [loading, setLoading] = useState(true);
    const [hideZeroSpend, setHideZeroSpend] = useState(false);
    const [selectedFranchise, setSelectedFranchise] = useState<string>('none');

    // Sorting State
    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<keyof CampaignData>('spend');

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // @ts-ignore
                ApiService.getCampaignStatistics(days).then((data: CampaignData[]) => {
                    setData(data);
                    setLoading(false);
                });
            } catch (error) {
                console.error("Failed to fetch campaign stats", error);
                setLoading(false);
            }
        };
        fetchData();
    }, [days]);

    // Derive unique franchises for filter
    const uniqueFranchises = useMemo(() => {
        const franchises = new Set(data.map(item => item.franchise));
        return Array.from(franchises).sort();
    }, [data]);

    // --- Sorting Logic ---
    const handleRequestSort = (property: keyof CampaignData) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const sortedData = useMemo(() => {
        if (!data) return [];

        let filteredData = [...data];

        if (hideZeroSpend) {
            filteredData = filteredData.filter(row => row.spend > 0);
        }

        if (selectedFranchise !== 'none') {
            filteredData = filteredData.filter(row => row.franchise === selectedFranchise);
        }

        return filteredData.sort((a, b) => {
            const aValue = a[orderBy];
            const bValue = b[orderBy];

            if (bValue < aValue) {
                return order === 'asc' ? 1 : -1;
            }
            if (bValue > aValue) {
                return order === 'asc' ? -1 : 1;
            }
            return 0;
        });
    }, [data, order, orderBy, hideZeroSpend, selectedFranchise]);

    // Calculate weighted averages per platform (weighted by spend)
    const platformAverages = useMemo(() => {
        const platforms = ['Google', 'Facebook'];
        const averages: { [key: string]: CampaignData & { campaignCount: number } } = {};

        platforms.forEach(platform => {
            const platformData = sortedData.filter(row => row.service === platform && row.spend > 0);
            const totalSpend = platformData.reduce((sum, row) => sum + row.spend, 0);

            if (totalSpend > 0 && platformData.length > 0) {
                // Weighted average helper: Σ(value × spend) / Σ(spend)
                const weightedAvg = (field: keyof CampaignData) => {
                    const sum = platformData.reduce((acc, row) => acc + (row[field] as number) * row.spend, 0);
                    return sum / totalSpend;
                };

                // For totals (not averages), just sum them
                const totalValueBooked = platformData.reduce((sum, row) => sum + row.value_booked, 0);
                const totalPlatformLeads = platformData.reduce((sum, row) => sum + row.platform_leads, 0);
                const totalInternalLeads = platformData.reduce((sum, row) => sum + row.internal_leads, 0);
                const totalBookings = platformData.reduce((sum, row) => sum + row.bookings_count, 0);

                // Recalculate derived metrics from totals
                const avgCpl = totalPlatformLeads > 0 ? totalSpend / totalPlatformLeads : 0;
                const avgCpa = totalBookings > 0 ? totalSpend / totalBookings : 0;
                const avgRoas = totalSpend > 0 ? totalValueBooked / totalSpend : 0;
                const avgConvRate = totalPlatformLeads > 0 ? (totalBookings / totalPlatformLeads) * 100 : 0;

                averages[platform] = {
                    id: -1,
                    campaign_id: '',
                    service: platform,
                    franchise: `Avg (${platformData.length})`,
                    spend: totalSpend,
                    value_booked: totalValueBooked,
                    platform_leads: totalPlatformLeads,
                    internal_leads: totalInternalLeads,
                    bookings_count: totalBookings,
                    cpl: avgCpl,
                    cpa: avgCpa,
                    conversion_rate: avgConvRate,
                    roas: avgRoas,
                    status: '',
                    campaignCount: platformData.length
                };
            }
        });

        return averages;
    }, [sortedData]);

    // Helper component for Table Headers
    const SortableHeader = ({ id, label, align = 'center', width }: { id: keyof CampaignData, label: string, align?: any, width?: string }) => {
        const active = orderBy === id;
        return (
            <th
                style={{ width: width, textAlign: align, cursor: 'pointer', userSelect: 'none' }}
                onClick={() => handleRequestSort(id)}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align === 'right' ? 'flex-end' : (align === 'center' ? 'center' : 'flex-start') }}>
                    {label}
                    {active ? (
                        order === 'desc' ?
                            <ArrowDownwardIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.8 }} /> :
                            <ArrowUpwardIcon sx={{ fontSize: 16, ml: 0.5, opacity: 0.8 }} />
                    ) : (
                        <Box sx={{ width: 16, height: 16, ml: 0.5 }} />
                    )}
                </Box>
            </th>
        );
    };

    return (
        <Box>
            <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'stretch', sm: 'flex-end' }, 
                alignItems: { xs: 'stretch', sm: 'center' }, 
                mb: 2, 
                gap: { xs: 1, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
            }}>
                <Select
                    value={selectedFranchise}
                    onChange={(_, val) => setSelectedFranchise(val as string)}
                    size="sm"
                    sx={{ minWidth: { xs: '100%', sm: 200 } }}
                >
                    <Option value="none">All Franchises</Option>
                    {uniqueFranchises.map((franchise) => (
                        <Option key={franchise} value={franchise}>
                            {franchise}
                        </Option>
                    ))}
                </Select>
                <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 }, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Checkbox
                        label="Hide Zero Spend"
                        checked={hideZeroSpend}
                        onChange={(event) => setHideZeroSpend(event.target.checked)}
                    />
                    <Select value={days} onChange={(_, val) => setDays(val as number)} size="sm" sx={{ minWidth: { xs: 'auto', sm: 'unset' } }}>
                        <Option value={30}>Last 30 Days</Option>
                        <Option value={60}>Last 60 Days</Option>
                        <Option value={120}>Last 120 Days</Option>
                    </Select>
                </Box>
            </Box>

            {loading ? (
                 <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
            ) : (
                <Sheet
                    variant="outlined"
                    sx={{
                        borderRadius: 'sm',
                        overflow: 'auto',
                        maxHeight: 'calc(100vh - 300px)',
                        boxShadow: 'sm'
                    }}
                >
                    <Table
                        stickyHeader
                        hoverRow
                        sx={{
                            '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                            '--Table-headerUnderlineThickness': '1px',
                            fontSize: 'xs',
                            '& th': { fontWeight: 700 },
                        }}
                    >
                        <thead>
                        <tr>
                            <SortableHeader id="service" label="Platform" align="left" />
                            <SortableHeader id="franchise" label="Franchise" align="left" />
                            <SortableHeader id="spend" label="Spend" align="right" />
                            <SortableHeader id="value_booked" label="Value Booked" align="right" />
                            <SortableHeader id="platform_leads" label="Platform Leads" align="right" />
                            <SortableHeader id="internal_leads" label="Internal Leads" align="right" />
                            <SortableHeader id="bookings_count" label="Bookings" align="right" />
                            <SortableHeader id="cpl" label="CPL" align="right" />
                            <SortableHeader id="cpa" label="CPA" align="right" />
                            <SortableHeader id="conversion_rate" label="Conv. Rate" align="right" />
                            <SortableHeader id="roas" label="ROAS" align="right" />
                        </tr>
                        {/* Sticky average rows per platform */}
                        {Object.entries(platformAverages).map(([platform, avg], idx) => {
                            const rowHeight = 37;
                            const bgColor = platform === 'Google' ? 'var(--joy-palette-primary-100)' : 'var(--joy-palette-warning-100)';
                            const isLast = idx === Object.keys(platformAverages).length - 1;
                            const cellStyle = {
                                background: bgColor,
                                fontWeight: 600,
                                height: rowHeight,
                                boxSizing: 'border-box' as const,
                                borderBottom: isLast ? '2px solid var(--joy-palette-divider)' : '1px solid var(--joy-palette-divider)',
                            };
                            return (
                                <tr 
                                    key={`avg-${platform}`}
                                    style={{ 
                                        position: 'sticky', 
                                        top: 36 + (idx * rowHeight),
                                        zIndex: 2 - idx,
                                        height: rowHeight,
                                    }}
                                >
                                    <th style={cellStyle}>{platform}</th>
                                    <th style={cellStyle}>{avg.franchise}</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>£{avg.spend.toFixed(2)}</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>£{avg.value_booked.toFixed(0)}</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>{avg.platform_leads.toFixed(0)}</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>{avg.internal_leads.toFixed(0)}</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>{avg.bookings_count.toFixed(0)}</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>£{avg.cpl.toFixed(2)}</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>£{avg.cpa.toFixed(2)}</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>{avg.conversion_rate.toFixed(1)}%</th>
                                    <th style={{ ...cellStyle, textAlign: 'right' }}>{avg.roas.toFixed(2)}x</th>
                                </tr>
                            );
                        })}
                        </thead>
                        <tbody>
                        {sortedData.map((row) => (
                            <tr key={row.id}>
                                <td>{row.service}</td>
                                <td>{row.franchise}</td>
                                <td style={{ textAlign: 'right' }}>
                                    {row.spend === 0 && row.platform_leads === 0 && row.internal_leads === 0 ? '-' : `£${row.spend.toFixed(2)}`}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    {row.value_booked === 0 ? '-' : `£${row.value_booked.toFixed(0)}`}
                                </td>
                                <td style={{ textAlign: 'right' }}>{row.platform_leads.toFixed(0)}</td>
                                <td style={{ textAlign: 'right' }}>{row.internal_leads}</td>
                                <td style={{ textAlign: 'right' }}>{row.bookings_count}</td>
                                <td style={{ textAlign: 'right' }}>
                                    {row.cpl === 0 ? '-' : `£${row.cpl.toFixed(2)}`}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    {row.cpa === 0 ? '-' : `£${row.cpa.toFixed(2)}`}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    {row.conversion_rate === 0 ? '-' : `${row.conversion_rate.toFixed(1)}%`}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                    {row.roas === 0 ? '-' : `${row.roas.toFixed(2)}x`}
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                </Sheet>
            )}
        </Box>
    );
}

const toDateInputValue = (date: Date) => date.toISOString().split('T')[0];

function CampaignLeakageContent() {
    const defaultEndDate = useMemo(() => new Date(), []);
    const defaultStartDate = useMemo(() => {
        const date = new Date();
        date.setDate(date.getDate() - 30);
        return date;
    }, []);

    const [startDate, setStartDate] = useState(toDateInputValue(defaultStartDate));
    const [endDate, setEndDate] = useState(toDateInputValue(defaultEndDate));
    const [data, setData] = useState<CampaignLeakageResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchLeakage = React.useCallback(() => {
        setLoading(true);
        setError(null);
        ApiService.getCampaignLeakage(startDate, endDate)
            .then((response: CampaignLeakageResponse) => {
                setData(response);
                setLoading(false);
            })
            .catch((err: any) => {
                setError(err?.response?.data?.error || 'Failed to fetch campaign leakage data.');
                setLoading(false);
            });
    }, [startDate, endDate]);

    useEffect(() => {
        fetchLeakage();
    }, [fetchLeakage]);

    const latestLeadLabel = (value: string) => {
        if (!value) return '-';
        return new Date(value).toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                alignItems: { xs: 'stretch', sm: 'flex-end' },
                gap: 2,
                mb: 2,
                flexDirection: { xs: 'column', sm: 'row' },
            }}>
                <FormControl size="sm">
                    <FormLabel>Lead Created From</FormLabel>
                    <Input type="date" value={startDate} onChange={(event) => setStartDate(event.target.value)} />
                </FormControl>
                <FormControl size="sm">
                    <FormLabel>Lead Created To</FormLabel>
                    <Input type="date" value={endDate} onChange={(event) => setEndDate(event.target.value)} />
                </FormControl>
                <Button size="sm" onClick={fetchLeakage} disabled={loading}>
                    {loading ? 'Loading...' : 'Refresh'}
                </Button>
            </Box>

            {error && (
                <Sheet color="danger" variant="soft" sx={{ p: 2, borderRadius: 'sm', mb: 2 }}>
                    <Typography color="danger">{error}</Typography>
                </Sheet>
            )}

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 2,
            }}>
                <Card variant="outlined">
                    <CardContent>
                        <Typography level="body-xs" textColor="text.tertiary">Mismatched Leads</Typography>
                        <Typography level="h3">{data?.summary.mismatched_leads ?? 0}</Typography>
                    </CardContent>
                </Card>
                <Card variant="outlined">
                    <CardContent>
                        <Typography level="body-xs" textColor="text.tertiary">Mismatch Groups</Typography>
                        <Typography level="h3">{data?.summary.mismatch_groups ?? 0}</Typography>
                    </CardContent>
                </Card>
                <Card variant="outlined">
                    <CardContent>
                        <Typography level="body-xs" textColor="text.tertiary">Compared Leads</Typography>
                        <Typography level="h3">{data?.summary.compared_leads ?? 0}</Typography>
                    </CardContent>
                </Card>
                <Card variant="outlined">
                    <CardContent>
                        <Typography level="body-xs" textColor="text.tertiary">Unrecognised Campaigns</Typography>
                        <Typography level="h3">{data?.summary.unrecognised_campaign_leads ?? 0}</Typography>
                    </CardContent>
                </Card>
            </Box>

            <Typography level="body-sm" textColor="text.tertiary" sx={{ mb: 2 }}>
                A mismatch means the lead's assigned franchise does not match the franchise name found in the campaign name.
            </Typography>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 'calc(100vh - 330px)', boxShadow: 'sm' }}>
                    <Table
                        stickyHeader
                        hoverRow
                        sx={{
                            '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                            '--Table-headerUnderlineThickness': '1px',
                            fontSize: 'xs',
                            '& th': { fontWeight: 700 },
                        }}
                    >
                        <thead>
                        <tr>
                            <th style={{ width: '12%' }}>Advertising Source</th>
                            <th style={{ width: '12%', textAlign: 'right' }}>Leads</th>
                            <th style={{ width: '16%' }}>Campaign Franchise</th>
                            <th style={{ width: '16%' }}>Lead Franchise</th>
                            <th style={{ width: '24%' }}>Campaign Name</th>
                            <th style={{ width: '12%' }}>Latest Lead</th>
                            <th style={{ width: '28%' }}>Examples</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(data?.rows || []).map((row) => (
                            <tr key={row.id}>
                                <td>{row.advertising_source}</td>
                                <td style={{ textAlign: 'right' }}>
                                    <Chip size="sm" color="danger" variant="soft">{row.lead_count}</Chip>
                                </td>
                                <td>{row.campaign_franchise}</td>
                                <td>{row.lead_franchise}</td>
                                <td>{row.campaign_name}</td>
                                <td>{latestLeadLabel(row.latest_created_at)}</td>
                                <td>
                                    {row.example_leads.map((lead) => (
                                        <Chip key={lead.id} size="sm" variant="soft" sx={{ mr: 0.5, mb: 0.5 }}>
                                            #{lead.id} {lead.customer}
                                        </Chip>
                                    ))}
                                </td>
                            </tr>
                        ))}
                        {data?.rows.length === 0 && (
                            <tr>
                                <td colSpan={7}>
                                    <Typography level="body-sm" textAlign="center" sx={{ py: 3 }}>
                                        No campaign/franchise mismatches found for this date range.
                                    </Typography>
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </Table>
                </Sheet>
            )}
        </Box>
    );
}

// Campaign Calculator Data Types
interface PlatformData {
    platform: string;
    total_spend: number;
    total_bookings: number;
    total_leads: number;
    value_booked: number;
    avg_daily_spend: number;
    avg_daily_bookings: number;
    avg_monthly_spend: number;
    avg_monthly_bookings: number;
    cpa: number;
    cpl: number;
    roas: number;
}

interface FranchiseCalculatorData {
    franchise: string;
    franchise_id: number;
    period_days: number;
    avg_booking_value: number;
    platforms: PlatformData[];
}

interface FranchiseOption {
    id: number;
    location: string;
}

// Campaign Calculator component
function CampaignCalculatorContent() {
    const [franchises, setFranchises] = useState<FranchiseOption[]>([]);
    const [franchiseData, setFranchiseData] = useState<FranchiseCalculatorData | null>(null);
    const [loadingFranchises, setLoadingFranchises] = useState(true);
    const [loadingData, setLoadingData] = useState(false);
    
    // Selectors
    const [selectedFranchiseId, setSelectedFranchiseId] = useState<number | null>(null);
    const [selectedPlatform, setSelectedPlatform] = useState<string>('');
    const [targetBookings, setTargetBookings] = useState<number>(25);
    const [budgetSplit, setBudgetSplit] = useState<number>(50); // Google %

    // Load franchises list on mount
    useEffect(() => {
        ApiService.getCampaignCalculatorFranchises().then((data: { franchises: FranchiseOption[] }) => {
            const sortedFranchises = (data.franchises || []).sort((a, b) => a.location.localeCompare(b.location));
            setFranchises(sortedFranchises);
            setLoadingFranchises(false);
            // Auto-select first franchise
            if (sortedFranchises.length > 0) {
                setSelectedFranchiseId(sortedFranchises[0].id);
            }
        }).catch(err => {
            console.error(err);
            setLoadingFranchises(false);
        });
    }, []);

    // Load franchise data when selection changes
    useEffect(() => {
        if (!selectedFranchiseId) return;
        
        setLoadingData(true);
        ApiService.getCampaignCalculatorData(selectedFranchiseId).then((data: FranchiseCalculatorData) => {
            setFranchiseData(data);
            setLoadingData(false);
            
            // Set default platform
            if (data.platforms && data.platforms.length > 0) {
                const hasGoogle = data.platforms.some(p => p.platform === 'Google');
                const hasFacebook = data.platforms.some(p => p.platform === 'Facebook');
                if (hasGoogle && hasFacebook) {
                    setSelectedPlatform('All');
                } else {
                    setSelectedPlatform(data.platforms[0].platform);
                }
            } else {
                setSelectedPlatform('');
            }
        }).catch(err => {
            console.error(err);
            setLoadingData(false);
            setFranchiseData(null);
        });
    }, [selectedFranchiseId]);

    // Available platforms for selected franchise
    const platforms = franchiseData?.platforms || [];
    const hasGoogle = platforms.some(p => p.platform === 'Google');
    const hasFacebook = platforms.some(p => p.platform === 'Facebook');
    const hasBoth = hasGoogle && hasFacebook;

    const metrics = useMemo(() => {
        if (!franchiseData || !selectedPlatform || platforms.length === 0) return null;

        const googleData = platforms.find(p => p.platform === 'Google');
        const fbData = platforms.find(p => p.platform === 'Facebook');

        if (selectedPlatform === 'All' && hasBoth && googleData && fbData) {
            // Mix logic with budget split
            const splitRatio = budgetSplit / 100;
            
            // Weighted CPA based on budget split (from actual historical 120-day data)
            const googleCpa = googleData.cpa || 150;
            const fbCpa = fbData.cpa || 100;
            const effectiveCpa = (splitRatio * googleCpa) + ((1 - splitRatio) * fbCpa);
            
            // Weighted ROAS
            const effectiveRoas = (splitRatio * googleData.roas) + ((1 - splitRatio) * fbData.roas);
            
            // Weighted CPL (from actual historical 120-day data)
            const effectiveCpl = (splitRatio * googleData.cpl) + ((1 - splitRatio) * fbData.cpl);
            
            // Combined monthly bookings
            const avgMonthlyBookings = googleData.avg_monthly_bookings + fbData.avg_monthly_bookings;

            return {
                cpa: effectiveCpa,
                cpl: effectiveCpl,
                roas: effectiveRoas,
                avgMonthlyBookings,
                isMixed: true
            };
        }

        // Single platform
        const platformData = platforms.find(p => p.platform === selectedPlatform);
        if (!platformData) return null;

        return {
            cpa: platformData.cpa || 150,
            cpl: platformData.cpl || 15,
            roas: platformData.roas || 3,
            avgMonthlyBookings: platformData.avg_monthly_bookings,
            isMixed: false
        };
    }, [franchiseData, platforms, selectedPlatform, hasBoth, budgetSplit]);

    if (loadingFranchises) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;

    // Scaling Logic - CPA increases gradually as you scale beyond historical average
    const historicalMonthlyBookings = metrics?.avgMonthlyBookings || 0;
    let scalingFactor = 1.0;
    
    // Diminishing returns model: uses square root for gradual increase, capped at 1.25x (25% max increase)
    if (metrics && targetBookings > historicalMonthlyBookings && historicalMonthlyBookings > 0) {
        const excessRatio = (targetBookings - historicalMonthlyBookings) / historicalMonthlyBookings;
        // Square root makes it more gradual: 2x bookings = ~14% increase, 4x = ~17%, etc.
        scalingFactor = 1 + (Math.sqrt(excessRatio) * 0.1);
        // Cap at 25% increase max
        scalingFactor = Math.min(scalingFactor, 1.25);
    } else if (metrics && historicalMonthlyBookings === 0) {
        if (targetBookings > 10) scalingFactor = 1.05; 
    }

    const projectedCpa = metrics ? metrics.cpa * scalingFactor : 0;
    const projectedCost = metrics ? targetBookings * projectedCpa : 0;
    const avgBookingValue = franchiseData?.avg_booking_value || 850;
    const projectedRevenue = targetBookings * avgBookingValue;
    const projectedCpl = metrics ? metrics.cpl * scalingFactor : 0;

    const selectedFranchiseName = franchises.find(f => f.id === selectedFranchiseId)?.location || '';

    return (
        <Box>
            <Typography level="body-sm" textColor="text.tertiary" mb={2}>
                CPA and CPL calculated from 120 days of Google/Facebook ad spend data (excluding last 20 days)
            </Typography>
            <Grid container spacing={3}>
                <Grid xs={12} md={5}>
                    <Card variant="outlined" sx={{ boxShadow: 'sm' }}>
                        <CardContent>
                            <Typography level="title-lg" mb={2}>Configuration</Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <FormControl>
                                    <FormLabel>Franchise</FormLabel>
                                    <Select 
                                        value={selectedFranchiseId} 
                                        onChange={(_, val) => setSelectedFranchiseId(val as number)}
                                    >
                                        {franchises.map(f => <Option key={f.id} value={f.id}>{f.location}</Option>)}
                                    </Select>
                                </FormControl>

                                {loadingData ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                                        <CircularProgress size="sm" />
                                    </Box>
                                ) : (
                                    <>
                                        <FormControl>
                                            <FormLabel>Platform</FormLabel>
                                            <Select 
                                                value={selectedPlatform} 
                                                onChange={(_, val) => setSelectedPlatform(val as string)}
                                                disabled={platforms.length === 0}
                                            >
                                                {hasBoth && <Option value="All">Combined (Mix)</Option>}
                                                {platforms.map(p => <Option key={p.platform} value={p.platform}>{p.platform}</Option>)}
                                            </Select>
                                        </FormControl>

                                        {selectedPlatform === 'All' && hasBoth && (
                                            <Box sx={{ px: 1, py: 1 }}>
                                                <Typography level="body-sm" mb={1}>Budget Split: Google {budgetSplit}% / Facebook {100 - budgetSplit}%</Typography>
                                                <Slider
                                                    value={budgetSplit}
                                                    onChange={(e, val) => setBudgetSplit(val as number)}
                                                    step={5}
                                                    min={0}
                                                    max={100}
                                                    valueLabelDisplay="auto"
                                                />
                                            </Box>
                                        )}

                                        <FormControl>
                                            <FormLabel>Target Bookings / Month</FormLabel>
                                            <Input 
                                                type="number" 
                                                value={targetBookings} 
                                                onChange={(e) => setTargetBookings(Number(e.target.value))} 
                                                slotProps={{ input: { min: 1 } }}
                                            />
                                            <Typography level="body-xs" textColor="text.tertiary" mt={0.5}>
                                                Historical Avg: {historicalMonthlyBookings.toFixed(1)} / month
                                            </Typography>
                                        </FormControl>
                                    </>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {metrics && !loadingData && (
                         <Card variant="soft" sx={{ mt: 2 }}>
                            <CardContent>
                                <Typography level="title-md">Historical Metrics (120 days)</Typography>
                                <Typography level="body-xs" mb={1}>From actual Google/Facebook ad spend data.</Typography>
                                <Table size="sm" sx={{ mt: 1, '& td': { py: 1 } }}>
                                    <tbody>
                                        <tr>
                                            <td>Historical CPA</td>
                                            <td style={{ textAlign: 'right' }}>£{metrics.cpa.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td>Historical CPL</td>
                                            <td style={{ textAlign: 'right' }}>£{metrics.cpl.toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td>Avg Booking Value</td>
                                            <td style={{ textAlign: 'right' }}>£{avgBookingValue.toFixed(0)}</td>
                                        </tr>
                                        {scalingFactor > 1 && (
                                            <tr>
                                                <td>Scaling Factor</td>
                                                <td style={{ textAlign: 'right' }}>{scalingFactor.toFixed(2)}x</td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </CardContent>
                         </Card>
                    )}
                </Grid>

                <Grid xs={12} md={7}>
                    {loadingData ? (
                        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', p: 5 }}>
                            <CircularProgress />
                        </Box>
                    ) : metrics ? (
                        <Card variant="outlined" sx={{ height: '100%', boxShadow: 'sm' }}>
                            <CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', gap: 4 }}>
                                <Box>
                                    <Typography level="body-md" textColor="text.tertiary" mb={1}>
                                        To achieve <b>{targetBookings} bookings</b> for {selectedFranchiseName} on {selectedPlatform === 'All' ? 'Mixed Platforms' : selectedPlatform}, it will cost approximately:
                                    </Typography>
                                    <Typography level="h1" color="primary">£{projectedCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                    <Typography level="body-xs" textColor="text.tertiary">
                                        Projected CPA: £{projectedCpa.toFixed(2)} {scalingFactor > 1 ? `(+${((scalingFactor - 1) * 100).toFixed(0)}% scaling)` : ''}
                                    </Typography>
                                </Box>
                                <Divider />
                                <Box>
                                    <Typography level="body-md" textColor="text.tertiary" mb={1}>
                                        And generate an estimated revenue of:
                                    </Typography>
                                    <Typography level="h1" color="success">£{projectedRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                    <Typography level="body-xs" textColor="text.tertiary">
                                        Based on £{avgBookingValue.toFixed(0)} avg roof clean price
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>
                    ) : (
                        <Box sx={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', p: 5, border: '1px dashed grey', borderRadius: 'sm', opacity: 0.5 }}>
                            <Typography textColor="text.tertiary">No campaign data available for this franchise.</Typography>
                        </Box>
                    )}
                </Grid>
            </Grid>
        </Box>
    );
}

// Return Calculator component
function ReturnCalculatorContent() {
    // Selectors state
    const [selectedFranchise, setSelectedFranchise] = useState<string | number>('none');
    const [selectedPlatform, setSelectedPlatform] = useState<string>('none');
    const [franchises, setFranchises] = useState<{ id: number; location: string }[]>([]);
    const [loadingData, setLoadingData] = useState<boolean>(false);

    // Input state
    const [expenses, setExpenses] = useState<number>(2500); // Default requested by user
    const [costPerAcquisition, setCostPerAcquisition] = useState<number>(150);
    const [bookingsPerMonth, setBookingsPerMonth] = useState<number>(10);
    const [avgBookingCost, setAvgBookingCost] = useState<number>(850);
    const [cancellationRate, setCancellationRate] = useState<number>(10);
    const [bookingsPerEmployee, setBookingsPerEmployee] = useState<number>(10);
    const [costPerEmployee, setCostPerEmployee] = useState<number>(2500);
    const [salesCommission, setSalesCommission] = useState<number>(50);

    // Fetch franchise list on mount
    useEffect(() => {
        ApiService.getAdminStatistics().then((data: NetworkOverviewResponse) => {
            if (data && data.franchises) {
                setFranchises(data.franchises.map(f => ({ id: f.id, location: f.location })).sort((a, b) => a.location.localeCompare(b.location)));
            }
        }).catch(err => console.error(err));
    }, []);

    // Fetch calculator data when selectors change
    useEffect(() => {
        const fetchData = async () => {
            if (selectedFranchise === 'none') {
                // Reset to network average defaults provided by user
                setCostPerAcquisition(150);
                setAvgBookingCost(850);
                setCancellationRate(10);
                setBookingsPerMonth(10);
                return;
            }

            setLoadingData(true);
            try {
                // @ts-ignore
                const data = await ApiService.getReturnCalculatorData(selectedFranchise, selectedPlatform);
                
                // Update state with fetched values
                if (data) {
                    setCostPerAcquisition(data.cost_per_acquisition || 150);
                    setAvgBookingCost(data.avg_booking_value || 850);
                    setCancellationRate(data.cancellation_rate || 10);
                    // Bookings per month from historical data
                    setBookingsPerMonth(Math.round(data.bookings_per_month) || 10); 
                }
            } catch (error) {
                console.error("Failed to fetch calculator data", error);
            } finally {
                setLoadingData(false);
            }
        };

        fetchData();
    }, [selectedFranchise, selectedPlatform]);

    // Calculations
    const totalMarketingCost = costPerAcquisition * bookingsPerMonth;
    
    // Effective bookings (after cancellations)
    const effectiveBookings = bookingsPerMonth * (1 - (cancellationRate / 100));
    const grossRevenue = effectiveBookings * avgBookingCost;
    
    // Franchise Fee (10% of Revenue)
    const franchiseFee = grossRevenue * 0.10;

    // Employee Costs
    const requiredEmployees = bookingsPerEmployee > 0 ? Math.ceil(bookingsPerMonth / bookingsPerEmployee) : 0;
    const totalEmployeeCost = requiredEmployees * costPerEmployee;

    // Sales Commission
    const totalSalesCommission = bookingsPerMonth * salesCommission;

    const totalCosts = expenses + totalMarketingCost + franchiseFee + totalEmployeeCost + totalSalesCommission;
    const netProfit = grossRevenue - totalCosts;
    const profitMargin = grossRevenue > 0 ? (netProfit / grossRevenue) * 100 : 0;
    const roiOnMarketing = totalMarketingCost > 0 ? ((grossRevenue - totalMarketingCost) / totalMarketingCost) * 100 : 0;

    return (
        <Box>
            {/* Selectors */}
            <Box sx={{ 
                display: 'flex', 
                justifyContent: { xs: 'stretch', sm: 'flex-end' }, 
                alignItems: { xs: 'stretch', sm: 'center' }, 
                mb: 3, 
                gap: { xs: 1.5, sm: 2 },
                flexDirection: { xs: 'column', sm: 'row' },
            }}>
                <FormControl size="sm" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <FormLabel>Franchise</FormLabel>
                    <Select
                        value={selectedFranchise}
                        onChange={(_, val) => setSelectedFranchise(val as string | number)}
                        placeholder="Select Franchise"
                        sx={{ minWidth: { xs: '100%', sm: 200 } }}
                    >
                        <Option value="none">Network Average</Option>
                        {franchises.map((f) => (
                            <Option key={f.id} value={f.id}>{f.location}</Option>
                        ))}
                    </Select>
                </FormControl>

                {selectedFranchise !== 'none' && (
                    <FormControl size="sm" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                        <FormLabel>Platform</FormLabel>
                        <Select
                            value={selectedPlatform}
                            onChange={(_, val) => setSelectedPlatform(val as string)}
                            placeholder="All Platforms"
                            sx={{ minWidth: { xs: '100%', sm: 150 } }}
                        >
                            <Option value="none">All Platforms</Option>
                            <Option value="Google">Google</Option>
                            <Option value="Facebook">Facebook</Option>
                        </Select>
                    </FormControl>
                )}
            </Box>

            {loadingData ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', width: '100%' }}>
                    <CircularProgress size="lg" />
                </Box>
            ) : (
                <Grid container spacing={3}>
                {/* Input Section */}
                <Grid xs={12} md={6}>
                    <Card variant="outlined" sx={{ boxShadow: 'sm' }}>
                        <CardContent>
                            <Typography level="title-lg" sx={{ mb: 3 }}>Input Parameters</Typography>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                <FormControl>
                                    <FormLabel>Monthly Expenses (£)</FormLabel>
                                    <Input
                                        type="number"
                                        value={expenses}
                                        onChange={(e) => setExpenses(Number(e.target.value) || 0)}
                                        startDecorator="£"
                                        slotProps={{ input: { min: 0 } }}
                                    />
                                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                        Fixed costs: rent, utilities, insurance, etc. (Excluding employee wages / marketing / sales commission / franchise fees)
                                    </Typography>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Cost / Employee (£)</FormLabel>
                                    <Input
                                        type="number"
                                        value={costPerEmployee}
                                        onChange={(e) => setCostPerEmployee(Number(e.target.value) || 0)}
                                        startDecorator="£"
                                        slotProps={{ input: { min: 0 } }}
                                    />
                                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                        Monthly cost per employee (Wages, NI, Pension)
                                    </Typography>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Cost Per Acquisition (£)</FormLabel>
                                    <Input
                                        type="number"
                                        value={costPerAcquisition}
                                        onChange={(e) => setCostPerAcquisition(Number(e.target.value) || 0)}
                                        startDecorator="£"
                                        slotProps={{ input: { min: 0 } }}
                                    />
                                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                        Marketing spend to acquire one booking
                                    </Typography>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Sales Commission (£)</FormLabel>
                                    <Input
                                        type="number"
                                        value={salesCommission}
                                        onChange={(e) => setSalesCommission(Number(e.target.value) || 0)}
                                        startDecorator="£"
                                        slotProps={{ input: { min: 0 } }}
                                    />
                                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                        Commission per booking
                                    </Typography>
                                </FormControl>

                                <Grid container spacing={2}>
                                    <Grid xs={6}>
                                        <FormControl>
                                            <FormLabel>Bookings / Month</FormLabel>
                                            <Input
                                                type="number"
                                                value={bookingsPerMonth}
                                                onChange={(e) => setBookingsPerMonth(Number(e.target.value) || 0)}
                                                slotProps={{ input: { min: 0 } }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={6}>
                                        <FormControl>
                                            <FormLabel>Cancellation Rate (%)</FormLabel>
                                            <Input
                                                type="number"
                                                value={cancellationRate}
                                                onChange={(e) => setCancellationRate(Number(e.target.value) || 0)}
                                                endDecorator="%"
                                                slotProps={{ input: { min: 0, max: 100 } }}
                                            />
                                        </FormControl>
                                    </Grid>
                                    <Grid xs={12}>
                                        <Typography level="body-xs" textColor="text.tertiary">
                                            {bookingsPerMonth} bookings - {cancellationRate}% cancellations = <b>{effectiveBookings.toFixed(1)} completed jobs</b>
                                        </Typography>
                                    </Grid>
                                </Grid>

                                <FormControl>
                                    <FormLabel>Average Booking Value (£)</FormLabel>
                                    <Input
                                        type="number"
                                        value={avgBookingCost}
                                        onChange={(e) => setAvgBookingCost(Number(e.target.value) || 0)}
                                        startDecorator="£"
                                        slotProps={{ input: { min: 0 } }}
                                    />
                                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                        Average revenue per completed booking (Roof Clean)
                                    </Typography>
                                </FormControl>

                                <FormControl>
                                    <FormLabel>Bookings / Employee</FormLabel>
                                    <Input
                                        type="number"
                                        value={bookingsPerEmployee}
                                        onChange={(e) => setBookingsPerEmployee(Number(e.target.value) || 0)}
                                        slotProps={{ input: { min: 1 } }}
                                    />
                                    <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                                        Capacity per employee. {requiredEmployees} employee(s) needed for {bookingsPerMonth} bookings/month.
                                    </Typography>
                                </FormControl>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Results Section */}
                <Grid xs={12} md={6}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Revenue Card */}
                        <Card variant="outlined" sx={{ boxShadow: 'sm' }}>
                            <CardContent>
                                <Typography level="title-md" textColor="text.tertiary">Gross Revenue</Typography>
                                <Typography level="h2" color="success">
                                    £{grossRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </Typography>
                                <Typography level="body-sm" textColor="text.tertiary">
                                    {effectiveBookings.toFixed(1)} completed jobs × £{avgBookingCost} avg value
                                </Typography>
                            </CardContent>
                        </Card>

                        {/* Costs Breakdown Card */}
                        <Card variant="outlined" sx={{ boxShadow: 'sm' }}>
                            <CardContent>
                                <Typography level="title-md" textColor="text.tertiary" sx={{ mb: 2 }}>Total Costs</Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography level="body-md">Fixed Expenses</Typography>
                                    <Typography level="body-md">£{expenses.toLocaleString()}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography level="body-md">Marketing Spend</Typography>
                                    <Typography level="body-md">£{totalMarketingCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography level="body-md">Sales Commission</Typography>
                                    <Typography level="body-md">£{totalSalesCommission.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography level="body-md">Franchise Fee (10%)</Typography>
                                    <Typography level="body-md">£{franchiseFee.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography level="body-md">Employee Costs ({requiredEmployees})</Typography>
                                    <Typography level="body-md">£{totalEmployeeCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                </Box>
                                <Divider sx={{ my: 1.5 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography level="title-md">Total</Typography>
                                    <Typography level="h3" color="danger">£{totalCosts.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Profit Card */}
                        <Card 
                            variant="solid" 
                            color={netProfit >= 0 ? 'success' : 'danger'}
                            invertedColors
                            sx={{ boxShadow: 'md' }}
                        >
                            <CardContent>
                                <Typography level="title-md">Net Profit / Loss</Typography>
                                <Typography level="h1">
                                    {netProfit >= 0 ? '' : '-'}£{Math.abs(netProfit).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 3, mt: 1 }}>
                                    <Box>
                                        <Typography level="body-xs">Profit Margin</Typography>
                                        <Typography level="title-lg">{profitMargin.toFixed(1)}%</Typography>
                                    </Box>
                                    <Box>
                                        <Typography level="body-xs">Marketing ROI</Typography>
                                        <Typography level="title-lg">{roiOnMarketing.toFixed(0)}%</Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Box>
                </Grid>

                {/* Scaling Table */}
                <Grid xs={12}>
                    <Card variant="outlined" sx={{ boxShadow: 'sm' }}>
                        <CardContent>
                            <Typography level="title-lg" sx={{ mb: 2 }}>Bookings Scaling Projection</Typography>
                            <Typography level="body-xs" mb={2}>Assuming {cancellationRate}% cancellation rate remains constant.</Typography>
                            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                                <Table
                                    sx={{
                                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                                        '--Table-headerUnderlineThickness': '1px',
                                        '& th': { fontWeight: 700 },
                                    }}
                                >
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'center' }}>Bookings/Month</th>
                                            <th style={{ textAlign: 'center' }}>Staff</th>
                                            <th style={{ textAlign: 'right' }}>Revenue</th>
                                            <th style={{ textAlign: 'right' }}>Marketing Cost</th>
                                            <th style={{ textAlign: 'right' }}>Total Costs</th>
                                            <th style={{ textAlign: 'right' }}>Net Profit</th>
                                            <th style={{ textAlign: 'right' }}>Margin</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[5, 10, 15, 20, 25, 30, 40, 50, 75, 100].map((bookings) => {
                                            const effective = bookings * (1 - (cancellationRate / 100));
                                            const rev = effective * avgBookingCost;
                                            const mktCost = bookings * costPerAcquisition;
                                            const fee = rev * 0.10;
                                            const employees = bookingsPerEmployee > 0 ? Math.ceil(bookings / bookingsPerEmployee) : 0;
                                            const empCost = employees * costPerEmployee;
                                            const commission = bookings * salesCommission;
                                            const total = expenses + mktCost + fee + empCost + commission;
                                            const profit = rev - total;
                                            const margin = rev > 0 ? (profit / rev) * 100 : 0;
                                            const isCurrentRow = bookings === bookingsPerMonth;
                                            
                                            return (
                                                <tr 
                                                    key={bookings}
                                                    style={{ 
                                                        backgroundColor: isCurrentRow ? 'var(--joy-palette-primary-softBg)' : undefined,
                                                        fontWeight: isCurrentRow ? 600 : undefined
                                                    }}
                                                >
                                                    <td style={{ textAlign: 'center' }}>
                                                        {bookings}
                                                        {isCurrentRow && <Chip size="sm" color="primary" sx={{ ml: 1 }}>Current</Chip>}
                                                    </td>
                                                    <td style={{ textAlign: 'center' }}>{employees}</td>
                                                    <td style={{ textAlign: 'right' }}>£{rev.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                                    <td style={{ textAlign: 'right' }}>£{mktCost.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                                    <td style={{ textAlign: 'right' }}>£{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                                    <td style={{ textAlign: 'right', color: profit >= 0 ? 'var(--joy-palette-success-500)' : 'var(--joy-palette-danger-500)' }}>
                                                        {profit >= 0 ? '' : '-'}£{Math.abs(profit).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                                                    </td>
                                                    <td style={{ textAlign: 'right' }}>{margin.toFixed(1)}%</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </Sheet>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
            )}
        </Box>
    );
}

// New Pricing Stats content component
function NewPricingContent() {
    const [stats, setStats] = useState<{
        total_leads_new_pricing: number;
        total_bookings_new_pricing: number;
        average_booking_price: number;
        roof_price_update?: {
            total_leads: number;
            total_bookings: number;
            average_booking_price: number;
        };
    } | null>(null);
    const [affluenceStats, setAffluenceStats] = useState<{ total_leads: number; total_bookings: number; average_booking_price: number } | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                const [newPricing, affluence] = await Promise.all([
                    ApiService.getNewPricingStats(),
                    ApiService.getAffluencePricingStats(),
                ]);
                setStats(newPricing);
                setAffluenceStats(affluence);
            } catch (err) {
                console.error("Failed to fetch pricing stats:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchStats();
    }, []);

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;
    }

    return (
        <Box>
            <Typography level="body-md" textColor="text.tertiary" sx={{ mb: 3 }}>
                Customers created on or after <Chip color="primary" variant="soft" size="sm">Feb 3rd, 2026</Chip> use the new pricing structure.
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3
            }}>
                <Card variant="solid" color="success" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Total Leads (New Pricing)</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            {stats?.total_leads_new_pricing?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            Leads assigned to a franchise
                        </Typography>
                    </CardContent>
                </Card>

                <Card variant="solid" color="primary" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Total Bookings (New Pricing)</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            {stats?.total_bookings_new_pricing?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            Bookings assigned to a franchise
                        </Typography>
                    </CardContent>
                </Card>

                <Card variant="solid" color="warning" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Average Booking Price</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            £{stats?.average_booking_price?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            From new pricing bookings
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography level="body-md" textColor="text.tertiary" sx={{ mb: 3 }}>
                Customers created on or after <Chip color="primary" variant="soft" size="sm">May 19th, 2026</Chip> use the latest roof clean pricing.
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3
            }}>
                <Card variant="solid" color="success" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Total Leads (Latest Pricing)</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            {stats?.roof_price_update?.total_leads?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            Leads assigned to a franchise
                        </Typography>
                    </CardContent>
                </Card>

                <Card variant="solid" color="primary" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Total Bookings (Latest Pricing)</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            {stats?.roof_price_update?.total_bookings?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            Bookings assigned to a franchise
                        </Typography>
                    </CardContent>
                </Card>

                <Card variant="solid" color="warning" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Average Booking Price</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            £{stats?.roof_price_update?.average_booking_price?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            From latest pricing bookings
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            <Divider sx={{ my: 4 }} />

            <Typography level="body-md" textColor="text.tertiary" sx={{ mb: 3 }}>
                Customers created on or after <Chip color="primary" variant="soft" size="sm">Apr 6th, 2026</Chip> use the affluence pricing structure.
            </Typography>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                gap: 3
            }}>
                <Card variant="solid" color="success" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Total Leads (Affluence Pricing)</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            {affluenceStats?.total_leads?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            Leads assigned to a franchise
                        </Typography>
                    </CardContent>
                </Card>

                <Card variant="solid" color="primary" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Total Bookings (Affluence Pricing)</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            {affluenceStats?.total_bookings?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            Bookings assigned to a franchise
                        </Typography>
                    </CardContent>
                </Card>

                <Card variant="solid" color="warning" invertedColors sx={{ boxShadow: 'md' }}>
                    <CardContent>
                        <Typography level="title-md">Average Booking Price</Typography>
                        <Typography level="h1" sx={{ mt: 1 }}>
                            £{affluenceStats?.average_booking_price?.toLocaleString() || 0}
                        </Typography>
                        <Typography level="body-sm" sx={{ mt: 1 }}>
                            From affluence pricing bookings
                        </Typography>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

function AdControlsContent() {
    const [rows, setRows] = useState<AdPlatformFranchiseRow[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchToggles = () => {
        setLoading(true);
        ApiService.getAdPlatformToggles()
            .then((data: AdPlatformFranchiseRow[]) => {
                setRows(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    };

    useEffect(() => {
        fetchToggles();
    }, []);

    const handleToggle = (franchiseId: number, field: 'facebook_sales_hours_enabled' | 'google_sales_hours_enabled' | 'sms_campaign_enabled', value: boolean) => {
        ApiService.setAdPlatformToggle(franchiseId, field, value)
            .then((updated: AdPlatformFranchiseRow) => {
                setRows(prev => prev.map(r => r.id === franchiseId ? { ...r, [field]: updated[field] } : r));
            })
            .catch(() => {});
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Control ad platforms and SMS marketing campaign participation for each franchise. Roof Ads Status is read-only.
            </Typography>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto' }}>
                <Table stickyHeader>
                    <thead>
                        <tr>
                            <th style={{ width: '20%' }}>Franchise</th>
                            <th style={{ width: '14%' }}>Roof Ads Status</th>
                            <th style={{ width: '22%' }}>Facebook Ads Enabled</th>
                            <th style={{ width: '22%' }}>Google Ads Enabled</th>
                            <th style={{ width: '22%' }}>SMS Campaign Enabled</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => (
                            <tr key={row.id}>
                                <td>{row.location}</td>
                                <td>
                                    <Chip
                                        size="sm"
                                        variant="soft"
                                        color={row.roof_ads_status === 'On' ? 'success' : row.roof_ads_status === 'Pending' ? 'warning' : 'neutral'}
                                    >
                                        {row.roof_ads_status}
                                    </Chip>
                                </td>
                                <td>
                                    <Switch
                                        checked={row.facebook_sales_hours_enabled}
                                        onChange={(e) => handleToggle(row.id, 'facebook_sales_hours_enabled', (e.target as HTMLInputElement).checked)}
                                    />
                                    {!row.has_facebook_campaign && (
                                        <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                                            No Facebook campaign configured for this franchise.
                                        </Typography>
                                    )}
                                </td>
                                <td>
                                    <Switch
                                        checked={row.google_sales_hours_enabled}
                                        onChange={(e) => handleToggle(row.id, 'google_sales_hours_enabled', (e.target as HTMLInputElement).checked)}
                                    />
                                    {!row.has_google_campaign && (
                                        <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                                            No Google campaign configured for this franchise.
                                        </Typography>
                                    )}
                                </td>
                                <td>
                                    <Switch
                                        checked={row.sms_campaign_enabled}
                                        onChange={(e) => handleToggle(row.id, 'sms_campaign_enabled', (e.target as HTMLInputElement).checked)}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>
        </Box>
    );
}

const SalesPerformanceKpiCard = ({
    title,
    value,
    caption
}: {
    title: string;
    value: string | number;
    caption?: string;
}) => (
    <Card variant="outlined" sx={{ height: '100%' }}>
        <CardContent>
            <Typography level="title-sm" textColor="text.secondary">{title}</Typography>
            <Typography level="h3" sx={{ mt: 0.5 }}>{value}</Typography>
            {caption && (
                <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>
                    {caption}
                </Typography>
            )}
        </CardContent>
    </Card>
);

const SpeedToLeadHeatmap = ({ days }: { days: SalesPerformanceDay[] }) => {
    const dayMap = new Map(days.map(day => [day.date, day]));
    const dates = days.map(day => new Date(`${day.date}T00:00:00Z`));
    const start = dates[0];
    const end = dates[dates.length - 1];

    if (!start || !end) {
        return (
            <Typography level="body-sm" textColor="text.tertiary">
                No speed-to-lead data available.
            </Typography>
        );
    }

    const leadingBlanks = Array((getDay(start) + 6) % 7).fill(null);
    const calendarDays = eachDayOfInterval({ start, end });
    const cells: (Date | null)[] = [...leadingBlanks, ...calendarDays];
    while (cells.length % 7 !== 0) cells.push(null);

    const getCellColor = (day: SalesPerformanceDay | undefined) => {
        if (!day || day.averageSpeedSeconds == null) return 'rgba(0, 0, 0, 0.06)';
        const minutes = day.averageSpeedSeconds / 60;
        if (minutes <= 5) return '#216e39';
        if (minutes <= 15) return '#30a14e';
        if (minutes <= 30) return '#40c463';
        if (minutes <= 60) return '#9be9a8';
        return '#ffebe9';
    };

    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', overflowX: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Typography level="title-md">Daily Speed-To-Lead</Typography>
                <Typography level="body-xs" textColor="text.tertiary">
                    Last 6 months, leads created 09:00-17:00 GMT
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ display: 'grid', gridTemplateRows: 'repeat(7, 12px)', gap: '3px', pt: '1px', minWidth: 24 }}>
                    {['Mon', '', 'Wed', '', 'Fri', '', ''].map((label, index) => (
                        <Typography key={`${label}-${index}`} level="body-xs" sx={{ fontSize: 9, lineHeight: '12px' }}>
                            {label}
                        </Typography>
                    ))}
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridAutoFlow: 'column',
                        gridTemplateRows: 'repeat(7, 12px)',
                        gap: '3px',
                    }}
                >
                    {cells.map((cell, index) => {
                        if (!cell) {
                            return <Box key={`blank-${index}`} sx={{ width: 12, height: 12 }} />;
                        }
                        const key = formatDateFns(cell, 'yyyy-MM-dd');
                        const day = dayMap.get(key);
                        const title = day?.formattedAverageSpeed
                            ? `${formatDateFns(cell, 'dd MMM yyyy')}: ${day.formattedAverageSpeed} avg from ${day.leadCount} lead${day.leadCount === 1 ? '' : 's'}`
                            : `${formatDateFns(cell, 'dd MMM yyyy')}: No qualifying leads`;
                        return (
                            <Tooltip key={key} title={title} size="sm">
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '2px',
                                        bgcolor: getCellColor(day),
                                        border: '1px solid rgba(27, 31, 36, 0.06)',
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, mt: 1 }}>
                <Typography level="body-xs">Faster</Typography>
                {['#216e39', '#30a14e', '#40c463', '#9be9a8', '#ffebe9'].map(color => (
                    <Box key={color} sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: color }} />
                ))}
                <Typography level="body-xs">Slower</Typography>
            </Box>
        </Sheet>
    );
};

const WorkedHoursHeatmap = ({ days }: { days: SalesWorkedHoursDay[] }) => {
    const dayMap = new Map(days.map(day => [day.date, day]));
    const dates = days.map(day => new Date(`${day.date}T00:00:00Z`));
    const start = dates[0];
    const end = dates[dates.length - 1];

    if (!start || !end) {
        return (
            <Typography level="body-sm" textColor="text.tertiary">
                No worked-hours data available.
            </Typography>
        );
    }

    const leadingBlanks = Array((getDay(start) + 6) % 7).fill(null);
    const calendarDays = eachDayOfInterval({ start, end });
    const cells: (Date | null)[] = [...leadingBlanks, ...calendarDays];
    while (cells.length % 7 !== 0) cells.push(null);

    const getCellColor = (day: SalesWorkedHoursDay | undefined) => {
        if (!day || day.workedSeconds == null) return 'rgba(0, 0, 0, 0.06)';
        const hours = day.workedSeconds / 3600;
        if (hours >= 7) return '#0e4429';
        if (hours >= 5) return '#006d32';
        if (hours >= 3) return '#26a641';
        if (hours >= 1) return '#39d353';
        return '#9be9a8';
    };

    const formatTime = (isoValue: string | null) => {
        if (!isoValue) return 'N/A';
        return formatDateFns(new Date(isoValue), 'ha').toLowerCase();
    };

    return (
        <Sheet variant="outlined" sx={{ p: 2, borderRadius: 'md', overflowX: 'auto' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                <Typography level="title-md">Daily Worked Hours Estimate</Typography>
                <Typography level="body-xs" textColor="text.tertiary">
                    First outbound sales call to last outbound sales call end, last 6 months
                </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ display: 'grid', gridTemplateRows: 'repeat(7, 12px)', gap: '3px', pt: '1px', minWidth: 24 }}>
                    {['Mon', '', 'Wed', '', 'Fri', '', ''].map((label, index) => (
                        <Typography key={`${label}-${index}`} level="body-xs" sx={{ fontSize: 9, lineHeight: '12px' }}>
                            {label}
                        </Typography>
                    ))}
                </Box>
                <Box
                    sx={{
                        display: 'grid',
                        gridAutoFlow: 'column',
                        gridTemplateRows: 'repeat(7, 12px)',
                        gap: '3px',
                    }}
                >
                    {cells.map((cell, index) => {
                        if (!cell) {
                            return <Box key={`worked-blank-${index}`} sx={{ width: 12, height: 12 }} />;
                        }
                        const key = formatDateFns(cell, 'yyyy-MM-dd');
                        const day = dayMap.get(key);
                        const callWindow = `${formatTime(day?.firstCallAt || null)} - ${formatTime(day?.lastCallAt || null)}`;
                        const title = day?.formattedWorkedTime
                            ? `${formatDateFns(cell, 'dd MMM yyyy')}: ${callWindow}, ${day.formattedWorkedTime} total window, ${day.formattedCallTime || '0s'} call time across ${day.callCount} calls`
                            : `${formatDateFns(cell, 'dd MMM yyyy')}: No outbound sales calls`;
                        return (
                            <Tooltip key={key} title={title} size="sm">
                                <Box
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        borderRadius: '2px',
                                        bgcolor: getCellColor(day),
                                        border: '1px solid rgba(27, 31, 36, 0.06)',
                                    }}
                                />
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.75, mt: 1 }}>
                <Typography level="body-xs">Less</Typography>
                {['#9be9a8', '#39d353', '#26a641', '#006d32', '#0e4429'].map(color => (
                    <Box key={color} sx={{ width: 12, height: 12, borderRadius: '2px', bgcolor: color }} />
                ))}
                <Typography level="body-xs">More</Typography>
            </Box>
        </Sheet>
    );
};

function SalesPerformanceTrackerContent() {
    const [data, setData] = useState<SalesPerformanceResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedSalespersonId, setSelectedSalespersonId] = useState<number | null>(null);
    const [showDatePickerModal, setShowDatePickerModal] = useState(false);

    const initialStartDate = useMemo(() => addDays(new Date(), -29), []);
    const initialEndDate = useMemo(() => new Date(), []);
    const [dateRange, setDateRange] = useState<DateRangeStateItem[]>([{
        startDate: initialStartDate,
        endDate: initialEndDate,
        key: 'selection'
    }]);

    useEffect(() => {
        const currentRange = dateRange[0];
        if (!currentRange.startDate || !currentRange.endDate) return;

        const abortController = new AbortController();
        setLoading(true);
        setError(null);

        ApiService.getSalesPerformanceTracker(
            selectedSalespersonId,
            formatDateFns(currentRange.startDate, 'dd/MM/yyyy'),
            formatDateFns(currentRange.endDate, 'dd/MM/yyyy'),
            abortController.signal
        )
            .then((response: SalesPerformanceResponse) => {
                if (abortController.signal.aborted) return;
                setData(response);
                if (!selectedSalespersonId && response.selectedSalespersonId) {
                    setSelectedSalespersonId(response.selectedSalespersonId);
                }
            })
            .catch((err: any) => {
                if (abortController.signal.aborted) return;
                console.error("Error fetching sales performance tracker:", err);
                setError(err.response?.data?.error || "Failed to load sales performance tracker.");
            })
            .finally(() => {
                if (!abortController.signal.aborted) {
                    setLoading(false);
                }
            });

        return () => abortController.abort();
    }, [dateRange, selectedSalespersonId]);

    const handleDateChange = (item: RangeKeyDict) => {
        const selection = item.selection as Range;
        if (selection?.startDate && selection?.endDate) {
            setDateRange([{
                startDate: selection.startDate,
                endDate: selection.endDate,
                key: 'selection'
            }]);
        }
    };

    const metrics = data?.metrics;
    const dateRangeLabel = dateRange[0].startDate && dateRange[0].endDate
        ? `${formatDateFns(dateRange[0].startDate, 'dd MMM yyyy')} - ${formatDateFns(dateRange[0].endDate, 'dd MMM yyyy')}`
        : 'Select Date Range';

    return (
        <Box>
            <Grid container spacing={2} sx={{ mb: 2, alignItems: 'stretch' }}>
                <Grid xs={12} md={4}>
                    <FormControl>
                        <FormLabel>Salesperson</FormLabel>
                        <Select
                            value={selectedSalespersonId}
                            placeholder="Select salesperson"
                            onChange={(_, value) => setSelectedSalespersonId(value as number)}
                            disabled={loading && !data}
                        >
                            {(data?.salespeople || []).map(person => (
                                <Option key={person.id} value={person.id}>
                                    {person.name}
                                </Option>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid xs={12} md={4} sx={{ display: { xs: 'block', md: 'none' } }}>
                    <Button
                        variant="outlined"
                        color="neutral"
                        startDecorator={<CalendarMonthRounded />}
                        onClick={() => setShowDatePickerModal(true)}
                        sx={{ width: '100%' }}
                    >
                        {dateRangeLabel}
                    </Button>
                </Grid>
            </Grid>

            <Modal open={showDatePickerModal} onClose={() => setShowDatePickerModal(false)}>
                <ModalDialog sx={{ width: '95vw', maxWidth: '400px', maxHeight: '90vh', overflow: 'auto', p: 2 }}>
                    <ModalClose />
                    <Typography level="title-lg" mb={2}>Select Date Range</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', '& .rdrCalendarWrapper': { width: '100%' } }}>
                        <DateRangePicker
                            onChange={handleDateChange}
                            showSelectionPreview={true}
                            moveRangeOnFirstSelection={false}
                            months={1}
                            ranges={dateRange}
                            direction="vertical"
                            preventSnapRefocus={true}
                            calendarFocus="backwards"
                            locale={enGB}
                            staticRanges={salesPerformanceStaticRanges}
                        />
                    </Box>
                    <Button sx={{ mt: 2 }} onClick={() => setShowDatePickerModal(false)}>Apply</Button>
                </ModalDialog>
            </Modal>

            <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                <Grid xs={12} md={4} lg={3} sx={{ display: { xs: 'none', md: 'flex' }, minWidth: 'fit-content' }}>
                    <Sheet
                        variant="outlined"
                        sx={{
                            minWidth: 'fit-content',
                            p: 1.5,
                            borderRadius: 'sm',
                            width: '100%',
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Typography level="title-md" mb={1} textAlign="center">Select Date Range</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <DateRangePicker
                                onChange={handleDateChange}
                                showSelectionPreview={true}
                                moveRangeOnFirstSelection={false}
                                months={1}
                                ranges={dateRange}
                                direction="vertical"
                                preventSnapRefocus={true}
                                calendarFocus="backwards"
                                locale={enGB}
                                staticRanges={salesPerformanceStaticRanges}
                            />
                        </Box>
                    </Sheet>
                </Grid>

                <Grid xs={12} md={8} lg={9}>
                    {loading && !data ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Sheet color="danger" variant="soft" sx={{ p: 2, borderRadius: 'sm' }}>
                            <Typography textColor="danger.700">{error}</Typography>
                        </Sheet>
                    ) : metrics ? (
                        <Grid container spacing={2} sx={{ alignItems: 'stretch' }}>
                            <Grid xs={12} sm={6} lg={4}>
                                <SalesPerformanceKpiCard
                                    title="Average calls per lead"
                                    value={formatMetricNumber(metrics.averageCallsPerLead, 2)}
                                    caption={`${metrics.totalLeads.toLocaleString()} handled leads`}
                                />
                            </Grid>
                            <Grid xs={12} sm={6} lg={4}>
                                <SalesPerformanceKpiCard
                                    title="Average phone call length"
                                    value={formatDurationFromSeconds(metrics.averagePhoneCallLengthSeconds)}
                                    caption="Answered outbound calls"
                                />
                            </Grid>
                            <Grid xs={12} sm={6} lg={4}>
                                <SalesPerformanceKpiCard
                                    title="Total time on phone"
                                    value={formatDurationFromSeconds(metrics.totalPhoneCallLengthSeconds)}
                                    caption={`${metrics.averageCalls.totalCalls.toLocaleString()} answered outbound calls`}
                                />
                            </Grid>
                            <Grid xs={12} sm={6} lg={4}>
                                <SalesPerformanceKpiCard
                                    title="Average calls"
                                    value={formatMetricNumber(metrics.averageCalls.average, 2)}
                                    caption={`${metrics.averageCalls.totalCalls.toLocaleString()} answered calls / ${metrics.averageCalls.totalLeads.toLocaleString()} leads`}
                                />
                            </Grid>
                            <Grid xs={12} sm={6} lg={4}>
                                <SalesPerformanceKpiCard
                                    title="Average attempted calls"
                                    value={formatMetricNumber(metrics.averageAttemptedCalls.average, 2)}
                                    caption={`${metrics.averageAttemptedCalls.totalCalls.toLocaleString()} attempts / ${metrics.averageAttemptedCalls.totalLeads.toLocaleString()} leads`}
                                />
                            </Grid>
                            <Grid xs={12} sm={6} lg={4}>
                                <SalesPerformanceKpiCard
                                    title="Google net conversion rate"
                                    value={`${formatMetricNumber(metrics.googleConversionRate.rate, 1)}%`}
                                    caption={`${metrics.googleConversionRate.bookings.toLocaleString()} non-cancelled bookings / ${metrics.googleConversionRate.leads.toLocaleString()} leads`}
                                />
                            </Grid>
                            <Grid xs={12} sm={6} lg={4}>
                                <SalesPerformanceKpiCard
                                    title="Facebook net conversion rate"
                                    value={`${formatMetricNumber(metrics.facebookConversionRate.rate, 1)}%`}
                                    caption={`${metrics.facebookConversionRate.bookings.toLocaleString()} non-cancelled bookings / ${metrics.facebookConversionRate.leads.toLocaleString()} leads`}
                                />
                            </Grid>
                        </Grid>
                    ) : (
                        <Typography level="body-sm" textColor="text.tertiary">
                            No sales performance data available.
                        </Typography>
                    )}
                </Grid>
            </Grid>

            <Box sx={{ mt: 3 }}>
                <SpeedToLeadHeatmap days={data?.speedToLead?.days || []} />
            </Box>

            <Box sx={{ mt: 3 }}>
                <WorkedHoursHeatmap days={data?.workedHours?.days || []} />
            </Box>
        </Box>
    );
}

// Main component with tabs
export default function FranchiseAdminOverview() {
    const [activeTab, setActiveTab] = useState<number>(0);

    return (
        <>
            {/* Header & Breadcrumbs */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Breadcrumbs
                    size="sm"
                    aria-label="breadcrumbs"
                    separator={<ChevronRightRoundedIcon />}
                    sx={{ pl: 0 }}
                >
                    <Link underline="none" color="neutral" href="/dashboard" aria-label="Home">
                        <HomeRoundedIcon />
                    </Link>
                    <Link underline="hover" color="neutral" href="/dashboard" fontSize={12} fontWeight={500}>
                        Dashboard
                    </Link>
                    <Typography color="primary" fontWeight={500} fontSize={12}>
                        Franchise Admin
                    </Typography>
                </Breadcrumbs>
            </Box>

            <Box sx={{ mb: 3 }}>
                <Typography level="h2" component="h1" sx={{ mb: 1 }}>
                    Franchise Admin
                </Typography>
            </Box>

            {/* Tab Navigation */}
            <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value as number)}
                sx={{ mb: 2 }}
            >
                <TabList
                    sx={{
                        borderRadius: 'lg',
                        bgcolor: 'background.level1',
                        p: 0.5,
                        gap: 0.5,
                        overflowX: 'auto',
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        flexWrap: { xs: 'nowrap', md: 'wrap' },
                        '& .MuiTab-root': {
                            borderRadius: 'md',
                            fontWeight: 'md',
                            flexShrink: 0,
                            minWidth: { xs: 'auto', sm: 'unset' },
                            fontSize: { xs: 'xs', sm: 'sm' },
                            px: { xs: 1.5, sm: 2 },
                        },
                        '& .Mui-selected': {
                            bgcolor: 'background.surface',
                            boxShadow: 'sm',
                        },
                    }}
                >
                    <Tab
                        value={0}
                        sx={{ gap: 1 }}
                    >
                        <PersonOff sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Unassigned </Box>Leads
                    </Tab>
                    <Tab
                        value={1}
                        sx={{ gap: 1 }}
                    >
                        <Dashboard sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        Overview
                    </Tab>
                    <Tab
                        value={2}
                        sx={{ gap: 1 }}
                    >
                        <CampaignIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        Campaigns
                    </Tab>
                    <Tab
                        value={11}
                        sx={{ gap: 1 }}
                    >
                        <AssessmentIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Sales </Box>Performance
                    </Tab>
                    <Tab
                        value={9}
                        sx={{ gap: 1 }}
                    >
                        <CampaignIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Campaign </Box>Leakage
                    </Tab>
                    <Tab
                        value={3}
                        sx={{ gap: 1 }}
                    >
                        <Calculate sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Campaign </Box>Calc
                    </Tab>
                    <Tab
                        value={4}
                        sx={{ gap: 1 }}
                    >
                        <Calculate sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Return </Box>Calc
                    </Tab>
                    <Tab
                        value={5}
                        sx={{ gap: 1 }}
                    >
                        <PriceCheck sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New </Box>Pricing
                    </Tab>
                    <Tab
                        value={6}
                        sx={{ gap: 1 }}
                    >
                        <Sms sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Bulk </Box>SMS
                    </Tab>
                    <Tab
                        value={10}
                        sx={{ gap: 1 }}
                    >
                        <WhatsApp sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Bulk </Box>WhatsApp
                    </Tab>
                    <Tab
                        value={7}
                        sx={{ gap: 1 }}
                    >
                        <ToggleOn sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Ad </Box>Controls
                    </Tab>
                    <Tab
                        value={8}
                        sx={{ gap: 1 }}
                    >
                        <MapIcon sx={{ fontSize: { xs: 16, sm: 18 } }} />
                        Territory
                    </Tab>
                </TabList>

                <TabPanel value={0} sx={{ p: 0, pt: 2 }}>
                    <UnassignedLeads />
                </TabPanel>

                <TabPanel value={1} sx={{ p: 0, pt: 2 }}>
                    <OverviewContent />
                </TabPanel>

                <TabPanel value={2} sx={{ p: 0, pt: 2 }}>
                    <CampaignsContent />
                </TabPanel>

                <TabPanel value={11} sx={{ p: 0, pt: 2 }}>
                    <SalesPerformanceTrackerContent />
                </TabPanel>

                <TabPanel value={9} sx={{ p: 0, pt: 2 }}>
                    <CampaignLeakageContent />
                </TabPanel>

                <TabPanel value={3} sx={{ p: 0, pt: 2 }}>
                    <CampaignCalculatorContent />
                </TabPanel>

                <TabPanel value={4} sx={{ p: 0, pt: 2 }}>
                    <ReturnCalculatorContent />
                </TabPanel>

                <TabPanel value={5} sx={{ p: 0, pt: 2 }}>
                    <NewPricingContent />
                </TabPanel>

                <TabPanel value={6} sx={{ p: 0, pt: 2 }}>
                    <BulkSMS />
                </TabPanel>

                <TabPanel value={10} sx={{ p: 0, pt: 2 }}>
                    <BulkWhatsApp />
                </TabPanel>

                <TabPanel value={7} sx={{ p: 0, pt: 2 }}>
                    <AdControlsContent />
                </TabPanel>

                <TabPanel value={8} sx={{ p: 0, pt: 2 }}>
                    <React.Suspense fallback={<Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>}>
                        <TerritoryMap />
                    </React.Suspense>
                </TabPanel>
            </Tabs>
        </>
    );
}